/**
 * Analytics Chart Component for v0.6.0
 * Displays ad views, clicks, and contacts over time
 */

import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import type { MemberAnalytics } from '../../../types';

interface AnalyticsChartProps {
  analytics: MemberAnalytics;
  className?: string;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const AnalyticsChart = ({ analytics, className = '' }: AnalyticsChartProps) => {
  // Prepare status distribution data for pie chart
  const statusData = Object.entries(analytics.ads.byStatus).map(([name, value]) => ({
    name: name === 'active' ? '已上架' : name === 'inactive' ? '已下架' : name === 'pending' ? '待審核' : name,
    value,
  }));

  // Prepare monthly contacts data for line chart
  const monthlyContactsData = Object.entries(analytics.contacts.byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, count]) => ({
      month: month.substring(5), // MM
      聯繫次數: count,
    }));

  // Prepare top viewed ads data
  const topViewedData = analytics.ads.topViewed.slice(0, 5).map(ad => ({
    name: ad.title.length > 15 ? ad.title.substring(0, 15) + '...' : ad.title,
    瀏覽: ad.views,
    點擊: ad.clicks,
  }));

  return (
    <div className={`analytics-chart ${className}`}>
      {/* Ads by Status */}
      <div className="analytics-chart__section">
        <h3 className="analytics-chart__section-title">廣告狀態分佈</h3>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={statusData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={(props) => `${props.name} ${props.percent ? (props.percent * 100).toFixed(0) : 0}%`}
              outerRadius={80}
              dataKey="value"
            >
              {statusData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Contacts */}
      <div className="analytics-chart__section">
        <h3 className="analytics-chart__section-title">每月聯繫趨勢</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={monthlyContactsData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="聯繫次數" stroke="#3b82f6" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Top Viewed Ads */}
      <div className="analytics-chart__section">
        <h3 className="analytics-chart__section-title">熱門廣告 (Top 5)</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={topViewedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="瀏覽" fill="#3b82f6" />
            <Bar dataKey="點擊" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AnalyticsChart;
